import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FaShieldAlt, FaLock, FaEye, FaUserCheck } from "react-icons/fa";
import bgImage from "../assets/bg.png";

const PrivacyContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  min-height: 100vh;
  background: url(${bgImage}) center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(
      circle at 20% 80%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%
    );
  z-index: 1;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
  color: white;
  z-index: 2;
  position: relative;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  opacity: 0.9;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ContentSection = styled.section`
  padding: 4rem 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);

  @media (max-width: 768px) {
    padding: 3rem 0;
  }

  @media (max-width: 480px) {
    padding: 2rem 0;
  }
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const Content = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 2rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 10px;
  }
`;

const Section = styled.div`
  margin-bottom: 2.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #005d3b;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;

  .icon {
    margin-right: 0.5rem;
    font-size: 1.2rem;
  }
`;

const SectionContent = styled.div`
  color: #333;
  line-height: 1.8;

  p {
    margin-bottom: 1rem;
  }

  ul {
    margin: 1rem 0;
    padding-left: 2rem;

    li {
      margin-bottom: 0.5rem;
    }
  }

  strong {
    color: #005d3b;
    font-weight: 600;
  }
`;

const LastUpdated = styled.div`
  text-align: center;
  color: #666;
  font-style: italic;
  margin-top: 2rem;
`;

const PrivacyPolicy = () => {
  return (
    <PrivacyContainer>
      <HeroSection id="privacy-hero">
        <BackgroundPattern />
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Privacy Policy
          </HeroTitle>

          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Your privacy is important to us. This policy explains how we
            collect, use, and protect your information.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <Content
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Section>
              <SectionTitle>
                <FaShieldAlt className="icon" />
                Information We Collect
              </SectionTitle>
              <SectionContent>
                <p>
                  We collect information you provide directly to us, such as
                  when you create an account, participate in our community
                  programs, or contact us for support.
                </p>
                <ul>
                  <li>
                    <strong>Personal Information:</strong> Name, email address,
                    phone number, and location
                  </li>
                  <li>
                    <strong>Community Data:</strong> Posts, comments, and
                    participation in community activities
                  </li>
                  <li>
                    <strong>Usage Information:</strong> How you interact with
                    our platform and services
                  </li>
                  <li>
                    <strong>Technical Data:</strong> IP address, browser type,
                    and device information
                  </li>
                </ul>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaEye className="icon" />
                How We Use Your Information
              </SectionTitle>
              <SectionContent>
                <p>We use the information we collect to:</p>
                <ul>
                  <li>Provide and improve our community-building services</li>
                  <li>Facilitate communication and collaboration</li>
                  <li>Send you important updates about our platform</li>
                  <li>Analyze usage patterns to enhance user experience</li>
                  <li>Ensure platform security and prevent abuse</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaLock className="icon" />
                Data Protection & Security
              </SectionTitle>
              <SectionContent>
                <p>
                  We implement appropriate technical and organizational measures
                  to protect your personal information against unauthorized
                  access, alteration, disclosure, or destruction.
                </p>
                <ul>
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security audits and assessments</li>
                  <li>Access controls and authentication measures</li>
                  <li>Staff training on data protection practices</li>
                </ul>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaUserCheck className="icon" />
                Your Rights & Choices
              </SectionTitle>
              <SectionContent>
                <p>You have the right to:</p>
                <ul>
                  <li>
                    <strong>Access:</strong> Request a copy of your personal
                    data
                  </li>
                  <li>
                    <strong>Correction:</strong> Update or correct inaccurate
                    information
                  </li>
                  <li>
                    <strong>Deletion:</strong> Request deletion of your personal
                    data
                  </li>
                  <li>
                    <strong>Portability:</strong> Receive your data in a
                    machine-readable format
                  </li>
                  <li>
                    <strong>Objection:</strong> Object to certain processing
                    activities
                  </li>
                </ul>
                <p>
                  To exercise these rights, please contact us at{" "}
                  <strong>info@wata-africa.com</strong>
                </p>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaShieldAlt className="icon" />
                Data Sharing & Third Parties
              </SectionTitle>
              <SectionContent>
                <p>
                  We do not sell, trade, or rent your personal information to
                  third parties. We may share information only in the following
                  circumstances:
                </p>
                <ul>
                  <li>With your explicit consent</li>
                  <li>To comply with legal requirements</li>
                  <li>To protect our rights and prevent fraud</li>
                  <li>
                    With trusted service providers who assist our operations
                  </li>
                </ul>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaEye className="icon" />
                Cookies & Tracking
              </SectionTitle>
              <SectionContent>
                <p>
                  We use cookies and similar technologies to enhance your
                  experience, analyze usage patterns, and provide personalized
                  content. You can control cookie preferences through your
                  browser settings.
                </p>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaLock className="icon" />
                International Data Transfers
              </SectionTitle>
              <SectionContent>
                <p>
                  Your information may be transferred to and processed in
                  countries other than your own. We ensure appropriate
                  safeguards are in place to protect your data during such
                  transfers.
                </p>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaUserCheck className="icon" />
                Children's Privacy
              </SectionTitle>
              <SectionContent>
                <p>
                  Our services are not directed to children under 13. We do not
                  knowingly collect personal information from children under 13.
                  If we become aware of such collection, we will take steps to
                  delete the information.
                </p>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaShieldAlt className="icon" />
                Changes to This Policy
              </SectionTitle>
              <SectionContent>
                <p>
                  We may update this privacy policy from time to time. We will
                  notify you of any material changes by posting the new policy
                  on our website and updating the "Last Updated" date.
                </p>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaEye className="icon" />
                Contact Us
              </SectionTitle>
              <SectionContent>
                <p>
                  If you have any questions about this privacy policy or our
                  data practices, please contact us:
                </p>
                <ul>
                  <li>
                    <strong>Email:</strong> info@wata-africa.com
                  </li>
                  <li>
                    <strong>Phone:</strong> +256 705813875
                  </li>
                  <li>
                    <strong>Address:</strong> 123 Water Tech Ave, Innovation
                    City
                  </li>
                </ul>
              </SectionContent>
            </Section>
          </Content>

          <LastUpdated>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Last Updated: {new Date().toLocaleDateString()}
            </motion.p>
          </LastUpdated>
        </Container>
      </ContentSection>
    </PrivacyContainer>
  );
};

export default PrivacyPolicy;
